import { useDispatch, useSelector } from 'react-redux';
import { Field, change } from 'redux-form';
import Price from 'components/Shared/Price';
import { CheckoutPageModel, isCheckoutPage } from 'models/CheckoutPageModel';
import { PageState } from 'models/PageState';
import { debounceP, hasElements, rm } from 'utils';
import { getQaDataAttr } from '@bygghemmait/utilities';
import { RenderCheckbox } from 'components/Shared/Form/inputs';
import { Button, LoaderButton } from 'components/Button';
import { useToggle } from 'hooks';
import IntegrityPolicyModal from 'views/CheckoutPage/PolicyInformation/integrityPolicyModal';
import { CheckoutModal } from 'views/CheckoutPage/checkoutModal';
import { LoadingStatus } from 'models/LoadingStatus';
import styles from './confirm.scss';
import { Terms } from 'views/CheckoutPage/Terms';
import { getTermsAccepted, selectShouldShowBHGNewsletter } from 'views/CheckoutPage/selector';
import { selectIsCompanyMode } from 'components/SiteMode/selector';
import { selectShouldUseNewCart } from 'views/CheckoutPage/DeliveryInformation/selector';
import { ChangeEvent, MouseEvent } from 'react';
import { Show } from '@bygghemmait/components';

interface Props {
  disabled?: boolean;
  handleSubmit: () => Promise<void>;
}

const selectSubmitDisabled = (state: PageState) =>
  state.currentPage.status === LoadingStatus.Fetching;

const SUBMIT_DELAY = 250;

const selectSubmitDisabledBobCart = ({ currentPage, cart }: PageState): boolean => {
  if (!isCheckoutPage(currentPage)) return false;

  const currentLoadingStatus = cart.status ?? LoadingStatus.Idle;
  const loadingStates = [LoadingStatus.Fetching, LoadingStatus.Loading];

  const isCartLoading = loadingStates.includes(currentLoadingStatus);
  const isPageLoading = loadingStates.includes(currentPage.status);
  const hasNoDeliveries = !hasElements(currentPage.deliveries);

  return isCartLoading || isPageLoading || hasNoDeliveries;
};

const getTotal = (state: PageState) => (state.currentPage as CheckoutPageModel).summary.total;

export const Confirm = ({ disabled, handleSubmit }: Props) => {
  const isBobCart = useSelector(selectShouldUseNewCart);
  const termsAccepted = useSelector(getTermsAccepted);

  const submitDisabled = useSelector(selectSubmitDisabled);
  const submitDisabledBobCart = useSelector(selectSubmitDisabledBobCart);
  const isCartLoading = isBobCart ? submitDisabledBobCart : submitDisabled;

  const debounceSubmit = debounceP(handleSubmit, SUBMIT_DELAY);
  const onSubmit = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    return debounceSubmit();
  };

  return (
    <div className={styles.confirm}>
      <NewsletterAndTerms />
      <Total />
      <LoaderButton
        {...getQaDataAttr('confirm')}
        type="submit"
        disabled={disabled || isCartLoading || !termsAccepted}
        onClick={onSubmit}
        appearance="quaternary"
        className={styles.submitButton}
      >
        {rm('checkout_confirm_order')}
      </LoaderButton>
    </div>
  );
};

const NEWSLETTER_FIELD = {
  NEWSLETTER: 'newsletter',
  BHG_NEWSLETTER: 'bhgNewsletter'
};

const NewsletterAndTerms = () => {
  const [modalOpen, toggleModal] = useToggle(false);

  return (
    <>
      <Newsletter toggleModal={toggleModal} />
      <BHGNewsletter />

      <Terms />

      <CheckoutModal onClose={toggleModal} show={modalOpen} contentClassName={styles.modal}>
        <IntegrityPolicyModal onClose={toggleModal} />
      </CheckoutModal>
    </>
  );
};

const BHGNewsletter = () => {
  const dispatch = useDispatch();
  const shouldShowBHGNewsletter = useSelector(selectShouldShowBHGNewsletter);
  if (!shouldShowBHGNewsletter) return null;

  const onBHGNewsletterChange = (e: ChangeEvent<HTMLInputElement>) => {
    const isEnabled = e.target.checked;
    if (!isEnabled) return;

    dispatch(change('checkout', NEWSLETTER_FIELD.NEWSLETTER, true));
  };

  return (
    <Show when={shouldShowBHGNewsletter}>
      <div className={styles.newsletter}>
        <Field
          name={NEWSLETTER_FIELD.BHG_NEWSLETTER}
          component={RenderCheckbox}
          className={styles.checkbox}
          appearance="secondary"
          color="white"
          onChange={onBHGNewsletterChange}
        />

        <span className={styles.policies}>
          <div className={styles.integrityPolicyInfo}>
            <strong>{rm('checkout_newsletter_yes')}</strong>
            ,&nbsp;
            {rm('checkout_newsletter_bhg_intro')}
            &nbsp;
            <Button
              href="//www.wearebhg.com/brands/"
              appearance="link"
              target="_blank"
              rel="noreferrer"
            >
              {rm('checkout_newsletter_bhg_others')}
            </Button>
            &nbsp;
            {rm('checkout_newsletter_bhg_unsubscribe')}
          </div>
        </span>
      </div>
    </Show>
  );
};

interface NewsletterProps {
  toggleModal: () => void;
}
const Newsletter = ({ toggleModal }: NewsletterProps) => {
  const dispatch = useDispatch();

  const onNewsletterChange = (e: ChangeEvent<HTMLInputElement>) => {
    const isEnabled = e.target.checked;
    if (isEnabled) return;

    dispatch(change('checkout', NEWSLETTER_FIELD.BHG_NEWSLETTER, false));
  };

  return (
    <div className={styles.newsletter}>
      <Field
        name={NEWSLETTER_FIELD.NEWSLETTER}
        component={RenderCheckbox}
        className={styles.checkbox}
        appearance="secondary"
        color="white"
        onChange={onNewsletterChange}
      />

      <span className={styles.policies}>
        <div className={styles.integrityPolicyInfo}>
          <strong>{rm('checkout_newsletter_yes')}</strong>
          ,&nbsp;
          {rm('checkout_newsletter_info')}
          &nbsp;
          <Button type="button" onClick={toggleModal} appearance="link">
            {rm('checkout_integrity_policy').toLowerCase()}.
          </Button>
          &nbsp;
          {rm('checkout_newsletter_unsubscribe')}
        </div>
      </span>
    </div>
  );
};

const Total = () => {
  const total = useSelector(getTotal);
  const isCompanyMode = useSelector(selectIsCompanyMode);

  return (
    <div className={styles.total}>
      <span className={styles.label}>
        {isCompanyMode ? rm('checkout_total_rd3_exl_vat') : rm('checkout_total_rd3')}
      </span>
      <Price className={styles.amount} price={total} appearance="discount" />
    </div>
  );
};
